import React from 'react'
import { usePageMeta } from '@/hooks/usePageMeta'

const SUPPORT_EMAIL = 'claritas.support@gmail.com'

function Section({ id, number, title, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-2xl font-bold font-display text-charcoal mb-4">
        <span className="text-gold">{number}</span> {title}
      </h2>
      <div className="space-y-4 text-base text-charcoal-muted leading-relaxed">
        {children}
      </div>
    </section>
  )
}

export default function Privacy() {
  usePageMeta({
    title: 'Privacy Policy | Claritas',
    description:
      'How Claritas Giving collects, uses, and protects your personal and financial information, including bank connections and donation processing via Stripe, and the Roundups program.',
  })

  return (
    <div className="min-h-screen bg-background-light">
      {/* Hero */}
      <section className="bg-white py-20 border-b border-surface-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
            Legal
          </p>
          <div className="w-8 h-px bg-gold mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl font-bold font-display text-charcoal mb-6">
            Privacy Policy
          </h1>
          <p className="text-sm text-charcoal-muted">
            Effective Date: October 31, 2026
          </p>
          <p className="text-sm text-charcoal-muted mt-1">
            Support Email:{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gold hover:underline">
              {SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="bg-background-light py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <p className="text-base text-charcoal-muted leading-relaxed">
            This Privacy Policy describes how Claritas Giving ("Claritas," "we," "us," or "our")
            collects, uses, shares, and protects information in connection with your use of the
            Claritas platform, including our websites, mobile applications, and related services
            (collectively, the "Platform"). Claritas is a charitable giving platform that connects
            donors with parishes and other religious organizations and enables one-time, recurring,
            and round-up donations. By creating an account or using the Platform, you agree to the
            practices described in this policy.
          </p>

          <Section id="info-we-collect" number="1" title="Information We Collect">
            <p>
              We collect information that you provide directly, information generated through your
              use of the Platform, and information we receive from the financial partners you
              connect. This includes, but is not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-charcoal">Account and contact information</strong> — your
                name, email address, and the parish or organization you choose to support. If you
                give as a guest without an account, we collect the email address you enter so we can
                send your receipt.
              </li>
              <li>
                <strong className="text-charcoal">Authentication data</strong> — credentials and
                session information managed through our authentication provider (Supabase Auth). We
                do not store your password in plain text. If you sign in with Apple or Google, we
                receive the name and email address that provider shares with us. With Sign in with
                Apple you may choose to hide your email, in which case we receive an Apple private
                relay address instead.
              </li>
              <li>
                <strong className="text-charcoal">Location and address searches</strong> — when you
                tap "Use My Current Location," we collect your device's precise location, with your
                permission, to find parishes near you. When you search by address, the text you type
                is used to suggest and locate addresses. If you are signed in, these requests are sent
                with your account credentials. We use your location only to answer that search and do
                not store it in your account or build a location history. You can change location
                permission at any time in your device settings.
              </li>
              <li>
                <strong className="text-charcoal">Activity in the app</strong> — the parishes you
                save or set as primary, events you RSVP to, parishes and people you block, and your
                notification preferences.
              </li>
              <li>
                <strong className="text-charcoal">Content you provide</strong> — answers to any
                questions a parish adds to its donation form, reports you submit about content or
                other users (including any notes), and, for parish administrators, the announcements,
                events, parish profile text, and photos you publish. When you choose a photo to
                upload, we receive only the photo you select; we do not otherwise access your photo
                library.
              </li>
              <li>
                <strong className="text-charcoal">Donation and transaction history</strong> — records
                of your one-time donations, recurring donations, and round-up contributions, including
                amounts, dates, payment status, and the recipient parish.
              </li>
              <li>
                <strong className="text-charcoal">Device and technical data</strong> — device
                identifiers and push-notification tokens, IP address, browser and operating system
                data, and app usage and diagnostic information. When you make a payment, Stripe also
                collects device and connection information to detect and prevent fraud.
              </li>
              <li>
                <strong className="text-charcoal">Communications data</strong> — messages and support
                requests you send to us and your notification preferences.
              </li>
            </ul>
            <p>
              <strong className="text-charcoal">Information from organizations.</strong> When we
              onboard a parish or Catholic organization, we collect nonprofit verification information
              such as its legal name, EIN, and supporting documents. These documents are stored
              privately and are accessible only to Claritas staff.
            </p>
          </Section>

          <Section id="financial-data" number="2" title="Financial and Banking Information">
            <p>
              Because Claritas processes donations and offers round-up giving, we handle financial
              information with particular care. We rely on regulated third-party financial partners and
              limit what we store on our own systems.
            </p>
            <p>
              <strong className="text-charcoal">Bank connections via Stripe Financial Connections.</strong> When
              you link a bank account, the connection is established and secured by{' '}
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">Stripe, Inc.</a> through its
              Financial Connections service. You enter your banking login credentials directly with
              Stripe and its trusted providers — <strong className="text-charcoal">Claritas never sees or stores
              your online banking username or password.</strong> Stripe provides us with a secure access
              token (which we store in encrypted form) and limited account and transaction data, which
              may include:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>The financial institution's name and your account's name, type, and subtype (e.g. checking, credit card).</li>
              <li>A masked account number (typically the last four digits only). We do not store full account or routing numbers.</li>
              <li>
                Transaction details used to calculate round-ups — including the transaction
                description, merchant name, amount, currency, date, category, and pending status.
              </li>
            </ul>
            <p>
              <strong className="text-charcoal">Payments via Stripe.</strong> All donation payments are
              processed by{' '}
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">Stripe, Inc.</a>, a PCI DSS
              Level 1 certified payment processor. Your full card or bank-account payment details are
              entered into and stored by Stripe, not by Claritas. We retain only non-sensitive
              references needed to process your donations, such as your Stripe customer identifier and a
              token representing your saved payment method.
            </p>
          </Section>

          <Section id="roundups" number="3" title="Roundups">
            <p>
              The Roundups program lets you round your everyday spending up to the next dollar and
              donate the accumulated difference to a parish you select. This section explains how it
              works and what information it involves.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-charcoal">Enabling Roundups.</strong> Round-ups are off by
                default. They become active only after you connect a bank account through Stripe
                Financial Connections, choose a recipient parish, and save a payment method through
                Stripe.
              </li>
              <li>
                <strong className="text-charcoal">How amounts are calculated.</strong> We periodically
                sync the transactions in your connected account and, for each eligible purchase,
                calculate the amount needed to round up to the next whole dollar. If you have enabled a
                boost multiplier, the rounded amount is multiplied accordingly.
              </li>
              <li>
                <strong className="text-charcoal">When donations are made.</strong> Round-ups are
                processed automatically on a weekly schedule, on a delay that allows your bank
                transactions to settle. The accumulated total for the week is donated to your chosen
                parish through Stripe. If the weekly total falls below the minimum processing amount, no
                charge is made and the amount carries forward.
              </li>
              <li>
                <strong className="text-charcoal">Records we keep.</strong> To run the program reliably
                and give you an accurate giving history, we maintain round-up settings (such as your
                selected parish and boost factor), per-week processing logs (including status, amount,
                and any error information), and summaries of your round-up donations. These records are
                tied to your account.
              </li>
              <li>
                <strong className="text-charcoal">Turning Roundups off.</strong> You can disable
                round-ups or disconnect your bank account at any time in the app. Doing so stops future
                round-up processing; donations already completed cannot be reversed through the Platform.
              </li>
            </ul>
          </Section>

          <Section id="how-we-use" number="4" title="How We Use Information">
            <p>We use the information described above for legitimate business purposes, including to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide, operate, and maintain the Platform and your account.</li>
              <li>Process one-time, recurring, and round-up donations and deliver funds to the recipient parish.</li>
              <li>Calculate round-up amounts and run the Roundups program.</li>
              <li>Find parishes near you or near an address you search for.</li>
              <li>Review reports, filter objectionable content, and enforce our community standards, including honoring the users and parishes you block.</li>
              <li>Authenticate users, secure the Platform, and prevent fraud and abuse.</li>
              <li>Send you service and transactional communications, receipts, and, where you have opted in, push notifications.</li>
              <li>We use your donation records to email a receipt for each successful donation and a year-end giving statement each January.</li>
              <li>Provide customer support and respond to your requests.</li>
              <li>Analyze and improve the Platform, and develop new features.</li>
              <li>Comply with legal, tax, and regulatory obligations and enforce our agreements.</li>
            </ul>
          </Section>

          <Section id="sharing" number="5" title="How We Share Information">
            <p>
              We do not sell your personal or financial information. We share information only as
              needed to operate the Platform and as described below:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-charcoal">Service providers.</strong> With Stripe (bank
                connectivity through Financial Connections, payment processing, and fraud
                prevention), Supabase (authentication and hosting), Google Maps Platform (address
                search suggestions and locating addresses), and push-notification providers, who
                process information on our behalf. When you type in the address search, the text you
                enter and your IP address are sent to Google and handled under the{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">Google Privacy Policy</a>.
              </li>
              <li>
                <strong className="text-charcoal">Sign-in providers.</strong> If you choose Sign in
                with Apple or Google, that provider processes your sign-in under its own privacy
                policy. When you delete your account, we ask Apple to revoke Claritas's access to your
                Apple sign-in.
              </li>
              <li>
                <strong className="text-charcoal">Other members.</strong> When you RSVP to an event,
                your display name may be shown to other signed-in members viewing that event's
                attendee list, unless they have blocked you. Announcements, events, and parish
                profiles published by parish administrators are visible to anyone using the Platform.
              </li>
              <li>
                <strong className="text-charcoal">Recipient parishes.</strong> When you donate to a
                parish, that organization receives information about your donation, which may include
                your name, donation amount, and date for the purposes of acknowledgment,
                record-keeping, and tax receipting. Each parish is responsible for its own handling of
                that information.
              </li>
              <li>
                <strong className="text-charcoal">Legal and safety.</strong> With governmental
                authorities or other parties where required by law, to protect our rights or the safety
                of others, or to prevent fraud or wrongdoing.
              </li>
              <li>
                <strong className="text-charcoal">Refund requests.</strong> If you ask for a refund,
                the request and the related donation details are shared between the organization and
                the Claritas staff who review it.
              </li>
              <li>
                <strong className="text-charcoal">Business transfers.</strong> In connection with a
                merger, acquisition, financing, or sale of assets, subject to this policy.
              </li>
            </ul>
          </Section>

          <Section id="security" number="6" title="Data Security">
            <p>
              We protect your information using industry-standard safeguards, including encryption of
              data in transit (TLS) and at rest, encrypted storage of financial access tokens, and
              role-based access controls enforced at the database level. While no system can be
              guaranteed perfectly secure, we work continuously to protect your information against
              unauthorized access, alteration, and disclosure. You can learn more on our{' '}
              <a href="/security" className="text-gold hover:underline">Security page</a>.
            </p>
          </Section>

          <Section id="retention" number="7" title="Data Retention">
            <p>
              We retain your information for as long as your account is active and as needed to provide
              the Platform. Donation and transaction records may be retained for longer periods where
              required for financial, tax, accounting, audit, or legal purposes. When information is no
              longer needed, we delete or de-identify it.
            </p>
            <p>
              <strong className="text-charcoal">Deleting your account.</strong> You can delete your
              account at any time in the app under Profile → Delete account. This permanently deletes
              your profile, saved parishes, RSVPs, blocks, and notification settings, cancels any
              recurring gifts, and disconnects linked bank accounts. Records of past donations are
              kept for the receiving parishes' tax and accounting records, but are no longer linked to
              an account.
            </p>
          </Section>

          <Section id="your-rights" number="8" title="Your Choices and Rights">
            <p>
              You may access and update your account information, manage your notification preferences,
              disable Roundups, disconnect linked bank accounts, remove saved payment methods, block
              users and parishes, and delete your account within the app. You can turn off location
              access, photo access, and push notifications at any time in your device settings.
              Depending on where you live, you may have additional rights — such as the right to
              access, correct, delete, or port your personal information, or to object to certain
              processing. To exercise these rights, contact us at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gold hover:underline">{SUPPORT_EMAIL}</a>.
              You can also disconnect Claritas's access to your financial data at any time through your
              Stripe Financial Connections settings.
            </p>
          </Section>

          <Section id="children" number="9" title="Children's Privacy">
            <p>
              The Platform is not directed to children under 13, and we do not knowingly collect
              personal information from children under 13. If you believe a child has provided us with
              personal information, please contact us so we can delete it.
            </p>
          </Section>

          <Section id="changes" number="10" title="Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. When we make material changes, we
              will update the Effective Date above and, where appropriate, provide additional notice.
              Your continued use of the Platform after an update constitutes acceptance of the revised
              policy.
            </p>
          </Section>

          <Section id="contact" number="11" title="Contact Us">
            <p>
              If you have questions about this Privacy Policy or how we handle your information, contact
              us at:
            </p>
            <p>
              <strong className="text-charcoal">Claritas Giving</strong>
              <br />
              Email:{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gold hover:underline">{SUPPORT_EMAIL}</a>
            </p>
          </Section>
        </div>
      </section>
    </div>
  )
}
